import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      common: {
        appName: 'FieldOps',
        startFreeTrial: 'Start Free Trial',
        bookADemo: 'Book a Demo',
        whatsappCta: 'Now in WhatsApp (Beta)',
        login: 'Login',
        openInNewTab: 'Open in a new tab',
        perYear: 'per year',
        total: 'Total',
        subtotal: 'Subtotal',
        costPerHectare: 'Cost per hectare',
        threeYearIncentive: '3-year incentive (20% off)',
        regionCoverageNote: '1,000,000 km² in this region'
      },
      hero: {
        badge: 'AI-Powered Agricultural Intelligence',
        headline1: 'FieldOps™',
        headline2: 'CPG\'s AI Co‑Pilot for Contract Farming',
        subhead: 'Agricultural AI Agents handling your workflows - from contracting to tracing field-level events',
        inspectExpect: '',//'You get what you <ins>INSPECT</ins>, not what you <exp>EXPECT</exp>.',
        ctaWatchDemo: 'Book a Demo',
        noCard: 'No credit card required • 150 hectares free for 12 months'
      },
      freeTrial: {
        bannerText: 'Get started with 150 ha free for 1 year'
      },
      footer: {
        ready: 'Ready to Transform Your Operations?',
        email: 'info@agcurate.com',
        phone: '+31 6 15 26 23 21',
        address: 'Meander 251, 6825 MC Arnhem, NL',
        copyright: '©2026 Agcurate B.V. All rights reserved.'
      },
      bookCall: {
        title: 'Book a call with us',
        desc: 'Pick a time that works for you. We’ll walk you through FieldOps and tailor the discussion to your crops, regions, and operational goals.'
      },
      pricingTable: {
        title: 'Transparent Pricing',
        subtitle: 'Choose the perfect plan that scales with your agricultural operations',
        basePlatform: 'Base Platform',
        advancedAddOn: 'Advanced Analytics Add‑On',
        plan: 'Plan',
        area: 'Area (ha)',
        pricePerHa: '€/ha/year',
        features: 'Features',
        freeTrialNote: 'Free Trial:',
        freeTrialCopy: '* First 150 ha free in year 1; thereafter tiered pricing (3 / 2.5 / 2 / 1.5 €/ha/year)',
        premiumFeatures: 'Premium Features Included:',
        regionalTitle: 'Regional Crop Intelligence',
        mostPopular: 'Most Popular',
        coverage: 'Pricing reflects ~1,000,000 km² coverage',
        discountBanner: 'when supplying requisite field truth data',
        discountPercent: '{{percent}}% discount',
        regional: {
          cropsNationwide_one: '{{count}} crop nationwide',
          cropsNationwide_other: '{{count}} crops nationwide',
          descOne: 'Perfect for focused operations',
          descThree: 'Most popular choice',
          descNine: 'Complete agricultural coverage'
        }
      },
      pricingCalc: {
        title: 'Pricing Calculator',
        subtitle: 'Get instant pricing based on your specific needs',
        areaLabel: 'Area (hectares)',
        analyticsServices: 'Analytics Services',
        selectAnalyticsPlaceholder: 'Select analytics services',
        analyticsYears: 'Analytics Years',
        yearsPlaceholder: 'Number of years',
        areaPlaceholder: 'Enter area in hectares',
        crops: 'Crops',
        selectCropsPlaceholder: 'Select crops',
        region: 'Region',
        selectRegionPlaceholder: 'Select region',
        years: 'Years',
        priceBreakdown: 'Price Breakdown',
        platformServices: 'Platform Services',
        base: 'Base Platform',
        advanced: 'Advanced Analytics',
        cropIntelligence: 'Crop Intelligence',
        packageLine: '{{countPrefix}}{{size}} crop{{sPlural}} × {{years}} year{{yPlural}}',
        letUsCallYou: 'Let us call you',
        getQuote: 'Get Quote'
      },
      valueProps: {
        sectionTitle: 'Complete Agricultural Intelligence',
        sectionSubtitle: 'Revolutionize your farming operations with cutting-edge technology and data-driven insights',
        visTitle: '360° Operational Visibility',
        visDesc: 'Track every field, farmer & input in one collaborative dashboard.',
        monTitle: 'Satellite‑Based Crop Monitoring',
        monDesc: '250+ vegetation & moisture indices from Sentinel‑2 imagery—no hardware needed.',
        mobTitle: 'Mobile Field App',
        mobDesc: 'Real‑time check‑ins, notes & alerts for iOS/Android teams in the field.',
        drawerVis: '360° Operational Visibility',
        drawerMon: 'Satellite‑Based Crop Monitoring',
        drawerMobTitle: 'Download FieldOps Mobile',
        drawerMobDesc: 'Get the FieldOps mobile app for real-time field management on iOS and Android',
        appStoreAlt: 'Download on App Store',
        playStoreAlt: 'Get it on Google Play'
      },
      kpi: {
        title: 'Proven Results',
        subtitle: 'Real impact from our agricultural intelligence platform',
        avgCostReduction: 'Average Cost Reduction',
        lessFieldTime: 'Less Field‑Visit Time',
        waterSavings: 'Water & Energy Savings',
        source: 'Source: FieldOps case study (2024‑2025)'
      },
      productTour: {
        title: 'Product Tour',
        subtitle: 'Explore FieldOps™ comprehensive suite of agricultural intelligence tools',
        tabs: {
          task: 'Task Management',
          crop: 'Crop Monitoring',
          analytics: 'Advanced Analytics',
          intelligence: 'Crop Intelligence'
        },
        badges: {
          essential: 'Essential',
          core: 'Core',
          premium: 'Premium',
          ai: 'AI-Powered'
        },
        bullets: {
          task: ['Staff & farmer registry', 'Digital check‑ins', 'Season summary', 'Team scoring'],
          crop: ['NDVI / NDMI / NDWI graphs', 'Weather overlays', 'Field benchmarking'],
          analytics: ['mm‑level irrigation', 'Leaf nitrogen', 'Disease early warning'],
          intelligence: ['National/provincial area & yield', '2 wks pre‑harvest forecasting']
        }
      },
      testimonials: {
        title: 'What Our Customers Say'
      },
      tech: {
        title: 'Technology Pipeline',
        subtitle: 'From raw data to actionable insights',
        poweredBy: 'Powered by industry leaders',
        steps: {
          dataFusion: 'Data Fusion',
          dataFusionDesc: 'Satellite imagery + weather + field data',
          ml: 'ML Models',
          mlDesc: 'Advanced algorithms for crop analysis',
          llm: 'LLM Agent',
          llmDesc: 'AI-powered insights and recommendations',
          actionable: 'Actionable Insights',
          actionableDesc: 'Real-time alerts and decision support'
        }
      },
      valueModel: {
        title: 'Value-Based Partnership',
        subtitle: 'FieldOps™ pricing is anchored to the operational value we unlock together—from reduced field costs to higher yields— so you invest in outcomes, not licenses.',
        pillars: {
          outcome: 'Outcome Alignment',
          outcomeDesc: 'We define the KPIs that matter most to your contract farming program before any pricing discussion.',
          upside: 'Shared Upside',
          upsideDesc: 'Investment scales with realized efficiency gains, yield improvements, or sustainability targets.',
          partnership: 'Partnership First',
          partnershipDesc: 'Dedicated agronomy analysts and product experts embedded with your teams during the season.'
        },
        blueprintTitle: 'Engagement Blueprint',
        blueprintDesc: 'How we transition from discovery to shared success metrics.',
        phases: {
          discover: 'Discover & Frame',
          discoverDesc: 'Joint workshop to map current operations, bottlenecks, and success metrics.',
          pilot: 'Pilot & Benchmark',
          pilotDesc: 'Launch a focused pilot to baseline performance and validate measurement approach.',
          measure: 'Measure Impact',
          measureDesc: 'Track agreed KPIs (cost-to-serve, inspection velocity, crop yield, ESG) in FieldOps dashboards.',
          scale: 'Scale & Share Value',
          scaleDesc: 'Roll out across regions with pricing tied to the value unlocked, not seats or hectares alone.'
        },
        signals: 'Signals We Track',
        signalsDesc: 'We measure value creation using mutually agreed scorecards and align commercial terms to those results.',
        why: 'Why Value-Based?',
        ctaTitle: 'Ready to co-design your model?',
        ctaDesc: 'Let’s map your operational goals and craft a value-sharing structure that fits.',
        ctaButton: 'Talk to our team'
      },
      signup: {
        title: 'Start Your Free Trial',
        desc: 'Get started with 150 hectares free for 12 months. No credit card required.',
        email: 'Email Address',
        org: 'Organization Name',
        emailPlaceholder: 'your@company.com',
        orgPlaceholder: 'Your Company Name',
        submit: 'Get Started',
        success: 'Please check your email for setup instructions!',
        error: 'Something went wrong. Please try again.'
      },
      whatsapp: {
        title: 'WhatsApp Early Access',
        desc: 'Get AgroGPT in WhatsApp for fast field questions. Share your details and we’ll invite you to the beta.',
        email: 'Work Email',
        emailPlaceholder: 'name@company.com',
        emailRequired: 'Please enter your email',
        emailInvalid: 'Please enter a valid email',
        name: 'Full Name',
        namePlaceholder: 'Your name',
        nameRequired: 'Please enter your name',
        org: 'Organization',
        orgPlaceholder: 'Company / program name',
        orgRequired: 'Please enter your organization',
        phone: 'Phone Number',
        phonePlaceholder: '+1 555 555 5555',
        phoneRequired: 'Please enter your phone number',
        submit: 'Request WhatsApp Access',
        success: 'Thanks! We will reach out with WhatsApp access details.',
        error: 'Could not submit your request. Please try again.'
      },
      quote: {
        title: 'Request a Custom Quote',
        intro: 'Tell us a bit about your program and we’ll follow up quickly with pricing tailored to your hectares, crops, and regions. We’ll get in contact if we need anything else.',
        breakdown: 'Pricing Breakdown',
        email: 'Work Email',
        org: 'Organization',
        orgPlaceholder: 'Company / Program name',
        area: 'Area (hectares)',
        areaPlaceholder: 'e.g., 5000',
        countries: 'Countries (comma-separated)',
        countriesPlaceholder: 'e.g., Netherlands, Germany',
        crops: 'Crops (comma-separated)',
        cropsPlaceholder: 'e.g., wheat, corn',
        role: 'Your Role',
        rolePlaceholder: 'Select your role',
        notes: 'Notes (optional)',
        notesPlaceholder: 'Anything else we should know?',
        submit: 'Request Quote',
        success: 'Thanks! We will contact you with a quote.',
        error: 'Could not submit your request. Please try again.'
      },
      verify: {
        passwordTitle: 'Set Your Password'
      },
      consent: {
        title: 'Privacy settings',
        description: 'We use cookies for analytics and advertising. Choose which categories to allow.',
        necessary: 'Necessary',
        necessaryDesc: 'Required for core functionality like security and session management.',
        analytics: 'Analytics',
        analyticsDesc: 'Helps us improve the website by collecting anonymized usage data.',
        advertising: 'Advertising',
        advertisingDesc: 'Used for advertising and retargeting on platforms like Meta and LinkedIn.',
        acceptAll: 'Accept all',
        rejectAll: 'Reject all',
        save: 'Save preferences'
      }
    }
  },
  nl: { 
    translation: {
      common: {
        appName: 'FieldOps',
        startFreeTrial: 'Start gratis proefperiode',
        bookADemo: 'Boek een demo',
        whatsappCta: 'Nu op WhatsApp (beta)',
        login: 'Inloggen',
        openInNewTab: 'Open in een nieuw tabblad',
        perYear: 'per jaar',
        total: 'Totaal',
        subtotal: 'Subtotaal',
        costPerHectare: 'Kosten per hectare',
        threeYearIncentive: '3‑jaars incentive (20% korting)',
        regionCoverageNote: '1.000.000 km² in deze regio'
      },
      hero: {
        badge: 'AI‑gestuurde landbouwintelligentie',
        headline1: 'FieldOps™',
        headline2: 'AI‑copiloot voor contractteelt bij CPG’s',
        subhead: 'AI‑agenten die uw workflows afhandelen — van contracteren tot het volgen van gebeurtenissen op perceelniveau',
        inspectExpect: '',
        ctaWatchDemo: 'Boek een demo',
        noCard: 'Geen creditcard nodig • 150 hectare gratis voor 12 maanden'
      },
      freeTrial: { bannerText: 'Begin met 150 ha gratis voor 1 jaar' },
      footer: {
        ready: 'Klaar om uw operaties te transformeren?',
        email: 'info@agcurate.com',
        phone: '+31 6 15 26 23 21',
        address: 'Meander 251, 6825 MC Arnhem, NL',
        copyright: '©2026 Agcurate B.V. Alle rechten voorbehouden.'
      },
      bookCall: {
        title: 'Plan een gesprek met ons',
        desc: 'Kies een tijd die voor u werkt. We leiden u door FieldOps en stemmen het gesprek af op uw gewassen, regio’s en doelen.'
      },
      pricingTable: {
        title: 'Transparante prijzen',
        subtitle: 'Kies het perfecte plan dat met uw landbouwactiviteiten meegroeit',
        basePlatform: 'Basisplatform',
        advancedAddOn: 'Advanced Analytics Add‑On',
        plan: 'Plan', area: 'Area (ha)', pricePerHa: '€/ha/jaar', features: 'Functies',
        freeTrialNote: 'Gratis proef:',
        freeTrialCopy: '* Eerste 150 ha gratis in jaar 1; daarna getrapte prijzen (3 / 2.5 / 2 / 1.5 €/ha/jaar)',
        regionalTitle: 'Regionale gewasintelligentie',
        mostPopular: 'Meest gekozen',
        coverage: 'Prijzen zijn gebaseerd op ~1.000.000 km² dekking',
        discountBanner: 'bij het aanleveren van vereiste veldwaarheidsgegevens',
        discountPercent: '{{percent}}% korting',
        regional: {
          cropsNationwide_one: '{{count}} gewas landelijk',
          cropsNationwide_other: '{{count}} gewassen landelijk',
          descOne: 'Perfect voor gerichte operaties',
          descThree: 'Meest populaire keuze',
          descNine: 'Volledige landelijke dekking'
        }
      },
      pricingCalc: {
        title: 'Prijscalculator',
        subtitle: 'Krijg direct een prijs op basis van uw behoeften',
        areaLabel: 'Oppervlakte (hectaren)',
        analyticsServices: 'Analysetools',
        selectAnalyticsPlaceholder: 'Selecteer analysetools',
        analyticsYears: 'AnalyJaren',
        yearsPlaceholder: 'Aantal jaren',
        areaPlaceholder: 'Voer oppervlakte in (hectaren)',
        crops: 'Gewassen',
        selectCropsPlaceholder: 'Selecteer gewassen',
        region: 'Regio',
        selectRegionPlaceholder: 'Selecteer regio',
        years: 'Jaren',
        priceBreakdown: 'Prijsopbouw',
        platformServices: 'Platformdiensten',
        base: 'Basisplatform',
        advanced: 'Geavanceerde analyses',
        cropIntelligence: 'Gewasintelligentie',
        letUsCallYou: 'Laat ons u bellen',
        getQuote: 'Ontvang offerte'
      },
      valueProps: {
        sectionTitle: 'Volledige Agrarische Intelligentie',
        sectionSubtitle: 'Transformeer uw landbouwactiviteiten met baanbrekende technologie en datagedreven inzichten',
        visTitle: '360° Operationele Zichtbaarheid',
        visDesc: 'Volg elk perceel, elke boer en elke input in één gezamenlijk dashboard.',
        monTitle: 'Satelliet‑gebaseerde Gewasmonitoring',
        monDesc: '250+ vegetatie‑ en vochtindices uit Sentinel‑2‑beelden — geen hardware nodig.',
        mobTitle: 'Mobiele Veldapp',
        mobDesc: 'Realtime check‑ins, notities en waarschuwingen voor iOS/Android‑teams in het veld.',
        drawerVis: '360° Operationele Zichtbaarheid',
        drawerMon: 'Satelliet‑gebaseerde Gewasmonitoring',
        drawerMobTitle: 'FieldOps Mobile downloaden',
        drawerMobDesc: 'Download de FieldOps‑app voor realtime veldbeheer op iOS en Android',
        appStoreAlt: 'Download in de App Store',
        playStoreAlt: 'Download op Google Play'
      },
      signup: {
        title: 'Start uw gratis proefperiode',
        desc: 'Begin met 150 hectare gratis voor 12 maanden. Geen creditcard nodig.',
        email: 'E‑mailadres', org: 'Organisatienaam',
        emailPlaceholder: 'jij@bedrijf.com', orgPlaceholder: 'Uw bedrijfsnaam',
        submit: 'Aan de slag', success: 'Controleer uw e‑mail voor instructies!', error: 'Er is iets misgegaan. Probeer het opnieuw.'
      },
      whatsapp: {
        title: 'WhatsApp Early Access',
        desc: 'Krijg AgroGPT in WhatsApp voor snelle veldvragen. Deel uw gegevens en we nodigen u uit voor de beta.',
        email: 'Zakelijk e‑mailadres',
        emailPlaceholder: 'naam@bedrijf.com',
        emailRequired: 'Vul uw e‑mail in',
        emailInvalid: 'Vul een geldig e‑mailadres in',
        name: 'Volledige naam',
        namePlaceholder: 'Uw naam',
        nameRequired: 'Vul uw naam in',
        org: 'Organisatie',
        orgPlaceholder: 'Bedrijfs- of programmanaam',
        orgRequired: 'Vul uw organisatie in',
        phone: 'WhatsApp‑nummer',
        phonePlaceholder: '+31 6 12 34 56 78',
        phoneRequired: 'Vul uw telefoonnummer in',
        submit: 'Vraag WhatsApp‑toegang aan',
        success: 'Bedankt! We nemen contact op over WhatsApp‑toegang.',
        error: 'Kon uw verzoek niet verzenden. Probeer opnieuw.'
      },
      quote: {
        title: 'Vraag een offerte aan',
        intro: 'Vertel ons iets over uw programma en wij komen snel bij u terug met een prijs op maat.',
        breakdown: 'Prijsopbouw', email: 'Zakelijk e‑mailadres', org: 'Organisatie', area: 'Oppervlakte (hectaren)', countries: 'Landen (komma‑gescheiden)', crops: 'Gewassen (komma‑gescheiden)', role: 'Uw rol', notes: 'Opmerkingen (optioneel)', submit: 'Offerte aanvragen', success: 'Bedankt! We nemen contact op met een offerte.', error: 'Kon uw verzoek niet verzenden. Probeer opnieuw.'
        , orgPlaceholder: 'Bedrijf / Programma naam', areaPlaceholder: 'bijv. 5000', countriesPlaceholder: 'bijv. Nederland, Duitsland', cropsPlaceholder: 'bijv. tarwe, maïs', rolePlaceholder: 'Selecteer uw rol', notesPlaceholder: 'Overige informatie?'
      },
      kpi: { title: 'Bewezen resultaten', subtitle: 'Echte impact van ons platform', avgCostReduction: 'Gemiddelde kostenreductie', lessFieldTime: 'Minder veldbezoektijd', waterSavings: 'Water‑ & energiebesparing', source: 'Bron: FieldOps casestudy (2024‑2025)' },
      consent: {
        title: 'Privacy-instellingen',
        description: 'We gebruiken cookies voor statistieken en advertenties. Kies welke categorieën u toestaat.',
        necessary: 'Noodzakelijk',
        necessaryDesc: 'Vereist voor basisfunctionaliteit zoals veiligheid en sessiebeheer.',
        analytics: 'Analyse',
        analyticsDesc: 'Helpt ons de website te verbeteren met geanonimiseerde gebruiksgegevens.',
        advertising: 'Advertenties',
        advertisingDesc: 'Gebruikt voor advertenties en retargeting op platforms zoals Meta en LinkedIn.',
        acceptAll: 'Alles accepteren',
        rejectAll: 'Alles weigeren',
        save: 'Voorkeuren opslaan'
      },
      productTour: { title: 'Producttour', subtitle: 'Ontdek de suite van FieldOps™', tabs: { task: 'Taakbeheer', crop: 'Gewasmonitoring', analytics: 'Geavanceerde analyses', intelligence: 'Gewasintelligentie' }, badges: { essential: 'Essentieel', core: 'Kern', premium: 'Premium', ai: 'AI‑gestuurd' }, bullets: { task: ['Medewerkers & boerenregister', 'Digitale check‑ins', 'Seizoenssamenvatting', 'Team scoring'], crop: ['NDVI / NDMI / NDWI grafieken', 'Weeroverlays', 'Vergelijking van percelen'], analytics: ['mm‑niveau irrigatie', 'Bladstikstof', 'Vroege ziektemelding'], intelligence: ['Nationaal/provinciaal areaal & opbrengst', '2 weken pre‑oogst forecast'] } },
      testimonials: { title: 'Wat onze klanten zeggen' },
      tech: { title: 'Technologiestack', subtitle: 'Van ruwe data naar acties', poweredBy: 'Aangedreven door marktleiders', steps: { dataFusion: 'Datafusie', dataFusionDesc: 'Satelliet + weer + velddata', ml: 'ML‑modellen', mlDesc: 'Geavanceerde algoritmen', llm: 'LLM‑agent', llmDesc: 'AI‑inzichten en aanbevelingen', actionable: 'Actieve inzichten', actionableDesc: 'Realtime alerts en beslissingsondersteuning' } },
      valueModel: { title: 'Waarde‑gebaseerde samenwerking', subtitle: 'FieldOps™ prijzen zijn gekoppeld aan de waarde die we samen realiseren…', pillars: { outcome: 'Resultaatsturing', outcomeDesc: 'We definiëren eerst de KPI’s die ertoe doen.', upside: 'Gedeelde groei', upsideDesc: 'Investering schaalt met efficiëntie en opbrengst.', partnership: 'Partnerschap eerst', partnershipDesc: 'Toegewijde teams binnen uw organisatie.' }, blueprintTitle: 'Engagement blauwdruk', blueprintDesc: 'Van ontdekking naar gedeelde metrics.', phases: { discover: 'Ontdekken & kaderen', discoverDesc: 'Gezamenlijke workshop…', pilot: 'Pilot & benchmark', pilotDesc: 'Gerichte pilot…', measure: 'Impact meten', measureDesc: 'KPIs volgen in dashboards.', scale: 'Opschalen & delen', scaleDesc: 'Uitrollen met waardegerichte prijs.' }, signals: 'Signalen die we volgen', signalsDesc: 'We meten waardecreatie met afgesproken scorecards.', why: 'Waarom waarde‑gebaseerd?', ctaTitle: 'Klaar om samen te ontwerpen?', ctaDesc: 'Laten we uw doelen mappen…', ctaButton: 'Praat met ons team' }
    }
  },
  tr: { 
    translation: {
      common: {
        appName: 'FieldOps',
        startFreeTrial: 'Ücretsiz Denemeyi Başlat',
        bookADemo: 'Demo Talep Et',
        whatsappCta: 'WhatsApp\'ta (Beta)',
        login: 'Giriş Yap',
        openInNewTab: 'Yeni sekmede aç',
        perYear: 'yıl başına',
        total: 'Toplam',
        subtotal: 'Ara toplam',
        costPerHectare: 'Hektar başına maliyet',
        threeYearIncentive: '3 yıllık teşvik (%20 indirim)',
        regionCoverageNote: 'Bu bölgede 1.000.000 km²'
      },
      hero: {
        badge: 'Yapay Zekâ Destekli Tarımsal Zekâ',
        headline1: 'FieldOps™',
        headline2: 'CPG’ler için sözleşmeli tarımda yapay zekâ yardımcı pilotu',
        subhead: 'Sözleşmeden saha olaylarının izlenmesine kadar iş akışlarınızı yöneten tarımsal yapay zekâ ajanları',
        inspectExpect: '',
        ctaWatchDemo: 'Demo Talep Et',
        noCard: 'Kredi kartı gerekmez • 12 ay boyunca 150 hektar ücretsiz'
      },
      freeTrial: { bannerText: '1 yıl boyunca 150 ha ücretsiz başlayın' },
      footer: {
        ready: 'Operasyonlarınızı dönüştürmeye hazır mısınız?',
        email: 'info@agcurate.com',
        phone: '+31 6 15 26 23 21',
        address: 'Meander 251, 6825 MC Arnhem, NL',
        copyright: '©2026 Agcurate B.V. Tüm hakları saklıdır.'
      },
      bookCall: {
        title: 'Bizimle görüşme planlayın',
        desc: 'Size uyan bir zaman seçin. FieldOps’u birlikte inceleyelim ve görüşmeyi ürünleriniz, bölgeleriniz ve hedeflerinize göre şekillendirelim.'
      },
      pricingTable: {
        title: 'Şeffaf Fiyatlandırma',
        subtitle: 'Tarım operasyonlarınızla ölçeklenen doğru planı seçin',
        basePlatform: 'Temel Platform',
        advancedAddOn: 'Gelişmiş Analitik Eklentisi',
        plan: 'Plan', area: 'Alan (ha)', pricePerHa: '€/ha/yıl', features: 'Özellikler',
        freeTrialNote: 'Ücretsiz Deneme:',
        freeTrialCopy: '* 1. yılda ilk 150 ha ücretsiz; sonrasında kademeli fiyat (3 / 2.5 / 2 / 1.5 €/ha/yıl)',
        regionalTitle: 'Bölgesel Ürün Zekâsı',
        mostPopular: 'En popüler',
        coverage: 'Fiyatlandırma ~1.000.000 km² kapsama göre belirlenmiştir',
        discountBanner: 'gerekli saha doğrulama verisi sağlandığında',
        discountPercent: '%{{percent}} indirim',
        regional: {
          cropsNationwide_one: '{{count}} ürün ülke çapında',
          cropsNationwide_other: '{{count}} ürün ülke çapında',
          descOne: 'Hedefe yönelik operasyonlar için ideal',
          descThree: 'En popüler tercih',
          descNine: 'Ülke çapında kapsamlı kapsama'
        }
      },
      pricingCalc: {
        title: 'Fiyat Hesaplayıcı',
        subtitle: 'İhtiyaçlarınıza göre anında fiyat alın',
        areaLabel: 'Alan (hektar)',
        analyticsServices: 'Analitik Servisler',
        selectAnalyticsPlaceholder: 'Analitik servisleri seçin',
        analyticsYears: 'Analitik Yılları',
        yearsPlaceholder: 'Yıl sayısı',
        areaPlaceholder: 'Alanı girin (hektar)',
        crops: 'Ürünler',
        selectCropsPlaceholder: 'Ürünleri seçin',
        region: 'Bölge',
        selectRegionPlaceholder: 'Bölge seçin',
        years: 'Yıl',
        priceBreakdown: 'Fiyat Dağılımı',
        platformServices: 'Platform Servisleri',
        base: 'Temel Platform',
        advanced: 'Gelişmiş Analitik',
        cropIntelligence: 'Ürün Zekâsı',
        letUsCallYou: 'Sizi arayalım',
        getQuote: 'Teklif Al'
      },
      signup: {
        title: 'Ücretsiz Denemeyi Başlatın',
        desc: '12 ay boyunca 150 hektar ücretsiz. Kredi kartı gerekmez.',
        email: 'E‑posta Adresi', org: 'Kurum Adı',
        emailPlaceholder: 'sizin@firma.com', orgPlaceholder: 'Kurum Adınız',
        submit: 'Başlayın', success: 'Kurulum talimatları için e‑postanızı kontrol edin!', error: 'Bir hata oluştu. Lütfen tekrar deneyin.'
      },
      whatsapp: {
        title: 'WhatsApp Erken Erişim',
        desc: 'AgroGPT’yi WhatsApp’ta kullanın. Bilgilerinizi paylaşın, beta daveti gönderelim.',
        email: 'İş E‑postası',
        emailPlaceholder: 'isim@firma.com',
        emailRequired: 'E‑posta adresinizi girin',
        emailInvalid: 'Geçerli bir e‑posta girin',
        name: 'Ad Soyad',
        namePlaceholder: 'Adınız',
        nameRequired: 'Adınızı girin',
        org: 'Kurum',
        orgPlaceholder: 'Şirket / program adı',
        orgRequired: 'Kurum adını girin',
        phone: 'WhatsApp Numarası',
        phonePlaceholder: '+90 5xx xxx xx xx',
        phoneRequired: 'Telefon numaranızı girin',
        submit: 'WhatsApp Erişimi İste',
        success: 'Teşekkürler! WhatsApp erişimi için size ulaşacağız.',
        error: 'Talebiniz gönderilemedi. Lütfen tekrar deneyin.'
      },
      valueProps: {
        sectionTitle: 'Kapsamlı Tarım Zekası',
        sectionSubtitle: 'Çiftçilik operasyonlarınızı ileri teknoloji ve veriye dayalı içgörülerle dönüştürün',
        visTitle: '360° Operasyonel Görünürlük',
        visDesc: 'Tüm tarlaları, çiftçileri ve girdileri tek bir ortak panoda takip edin.',
        monTitle: 'Uydu Tabanlı Ürün İzleme',
        monDesc: 'Donanıma gerek olmadan Sentinel‑2’den 250+ bitki örtüsü ve nem indeksi.',
        mobTitle: 'Mobil Saha Uygulaması',
        mobDesc: 'iOS/Android ekipleri için gerçek zamanlı girişler, notlar ve uyarılar.',
        drawerVis: '360° Operasyonel Görünürlük',
        drawerMon: 'Uydu Tabanlı Ürün İzleme',
        drawerMobTitle: 'FieldOps Mobile’ı İndirin',
        drawerMobDesc: 'iOS ve Android için gerçek zamanlı saha yönetimi',
        appStoreAlt: 'App Store’dan indirin',
        playStoreAlt: 'Google Play’den indirin'
      },

      quote: {
        title: 'Özel Teklif İsteği',
        intro: 'Programınız hakkında kısaca bilgi verin; hektar, ürün ve bölgelere göre hızlıca fiyat paylaşalım.',
        breakdown: 'Fiyat Dağılımı', email: 'İş E‑postası', org: 'Kurum', area: 'Alan (hektar)', countries: 'Ülkeler (virgülle ayrılmış)', crops: 'Ürünler (virgülle ayrılmış)', role: 'Rolünüz', notes: 'Notlar (opsiyonel)', submit: 'Teklif Al', success: 'Teşekkürler! En kısa sürede teklif ile dönüş yapacağız.', error: 'Talebiniz gönderilemedi. Lütfen tekrar deneyin.',
        orgPlaceholder: 'Şirket / Program adı', areaPlaceholder: 'örn. 5000', countriesPlaceholder: 'örn. Hollanda, Almanya', cropsPlaceholder: 'örn. buğday, mısır', rolePlaceholder: 'Rolünüzü seçin', notesPlaceholder: 'Başka eklemek istediğiniz bir şey?'
      },
      kpi: { title: 'Kanıtlanmış Sonuçlar', subtitle: 'Platformumuzun gerçek etkisi', avgCostReduction: 'Ortalama Maliyet Azalımı', lessFieldTime: 'Daha Az Saha Zamanı', waterSavings: 'Su & Enerji Tasarrufu', source: 'Kaynak: FieldOps vaka çalışması (2024‑2025)' },
      consent: {
        title: 'Gizlilik ayarları',
        description: 'Analitik ve reklam amaçlı çerezler kullanıyoruz. İzin vermek istediğiniz kategorileri seçin.',
        necessary: 'Gerekli',
        necessaryDesc: 'Güvenlik ve oturum gibi temel işlevler için zorunludur.',
        analytics: 'Analitik',
        analyticsDesc: 'Siteyi iyileştirmek için anonim kullanım verileri toplar.',
        advertising: 'Reklam',
        advertisingDesc: 'Meta ve LinkedIn gibi platformlarda reklam ve yeniden hedefleme için kullanılır.',
        acceptAll: 'Tümünü kabul et',
        rejectAll: 'Tümünü reddet',
        save: 'Tercihleri kaydet'
      },
      productTour: { title: 'Ürün Turu', subtitle: 'FieldOps™ çözümlerini keşfedin', tabs: { task: 'Görev Yönetimi', crop: 'Ürün İzleme', analytics: 'Gelişmiş Analitik', intelligence: 'Ürün Zekâsı' }, badges: { essential: 'Temel', core: 'Çekirdek', premium: 'Premium', ai: 'Yapay zekâ destekli' }, bullets: { task: ['Personel & çiftçi kayıtları', 'Dijital check‑in', 'Sezon özeti', 'Ekip skoru'], crop: ['NDVI / NDMI / NDWI grafikler', 'Hava durumu katmanları', 'Tarla karşılaştırma'], analytics: ['mm düzeyi sulama', 'Yaprak azotu', 'Hastalık erken uyarı'], intelligence: ['Ulusal/il düzeyi alan & verim', 'Hasattan 2 hafta önce tahmin'] } },
      testimonials: { title: 'Müşterilerimiz Ne Diyor' },
      tech: { title: 'Teknoloji Hattı', subtitle: 'Ham veriden aksiyona', poweredBy: 'Sektör liderleri tarafından desteklenmektedir', steps: { dataFusion: 'Veri Füzyonu', dataFusionDesc: 'Uydu + hava + saha verisi', ml: 'ML Modelleri', mlDesc: 'Gelişmiş algoritmalar', llm: 'LLM Ajanı', llmDesc: 'Yapay zekâ içgörüleri', actionable: 'Eyleme Dönük İçgörüler', actionableDesc: 'Gerçek zamanlı uyarılar ve karar desteği' } },
      valueModel: { title: 'Değere Dayalı İş Birliği', subtitle: 'FieldOps™ fiyatlandırması birlikte yarattığımız değere bağlıdır…', pillars: { outcome: 'Sonuç Odaklılık', outcomeDesc: 'Önce önemli KPI’ları tanımlarız.', upside: 'Paylaşılan Kazanç', upsideDesc: 'Yatırım, verimlilik ve çıktı ile ölçeklenir.', partnership: 'Önce Ortaklık', partnershipDesc: 'Sezon boyunca ekiplerinizle birlikte çalışan uzmanlar.' }, blueprintTitle: 'İş Birliği Yol Haritası', blueprintDesc: 'Keşiften ortak metriklere geçiş.', phases: { discover: 'Keşfet & Çerçevele', discoverDesc: 'Mevcut süreçleri ve KPI’ları birlikte çıkarırız.', pilot: 'Pilot & Kıyasla', pilotDesc: 'Odaklı pilot ile metrikleri doğrularız.', measure: 'Etkisini Ölç', measureDesc: 'Anlaşılmış KPI’ları panolarda izleriz.', scale: 'Ölçekle & Değeri Paylaş', scaleDesc: 'Değere bağlı fiyatla yaygınlaştırırız.' }, signals: 'Takip ettiğimiz göstergeler', signalsDesc: 'Değer yaratımını ortak skor kartlarıyla ölçeriz.', why: 'Neden Değere Dayalı?', ctaTitle: 'Modelinizi birlikte tasarlamaya hazır mısınız?', ctaDesc: 'Hedeflerinizi eşleyip uygun yapıyı kuralım.', ctaButton: 'Ekibimizle iletişime geçin' }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'nl', 'tr'],
    interpolation: { escapeValue: false }
  });

export default i18n;
      
      
